import SanityImage from '@otovo/shared/components/Sanity/SanityImage';
import { useGlobalConfig } from '@otovo/shared/hooks/configContext';
import { Sanity$BlogPage } from '@otovo/shared/types/sanityTypes';
import Link from 'next/link';
import { FormattedMessage as T } from 'react-intl';
import Badge from '../../v2/Badge/Badge';
import m from '../messages';

type Props = {
  featuredPost: Sanity$BlogPage;
  linkToPost?: boolean;
};

const FeaturedBlogPost = ({ featuredPost, linkToPost = false }: Props) => {
  const config = useGlobalConfig();
  const mainTag = featuredPost.tags[0];

  const title = linkToPost ? (
    <Link
      href={`${config.STOREFRONT_URL}/blog/${mainTag.slug.current}/${featuredPost.slug.current}/`}
    >
      {featuredPost.title}
    </Link>
  ) : (
    featuredPost.title
  );

  const image = (
    <SanityImage
      fill
      priority
      sizes="(max-width: 1024px) 100vw, 50vw"
      className="object-cover"
      image={featuredPost.mainImage}
      alt={featuredPost.mainImage.alt}
    />
  );

  const tag = (
    <Badge
      text={
        <Link
          className="text-xs font-semibold text-grey-90 uppercase"
          href={`${config.STOREFRONT_URL}/blog/${mainTag.slug.current}/`}
        >
          {mainTag.title}
        </Link>
      }
    />
  );

  const author = (
    <p className="mt-2 text-sm text-grey-90 lg:text-heading">
      <T {...m.writtenByPrefix} />{' '}
      <Link
        className="underline hover:no-underline"
        href={`${config.STOREFRONT_URL}/blog/author/${featuredPost.author.slug.current}/`}
      >
        {featuredPost.author.name}
      </Link>
    </p>
  );

  return (
    <section className="mx-auto mt-22 mb-8 max-w-7xl px-4 md:px-8 lg:mt-48 lg:mb-24 lg:px-12">
      <div className="relative lg:grid lg:grid-cols-2 lg:items-center lg:gap-12">
        <div className="relative h-[28rem] overflow-hidden rounded-2xl lg:h-[38rem]">
          {linkToPost ? (
            <Link
              href={`${config.STOREFRONT_URL}/blog/${mainTag.slug.current}/${featuredPost.slug.current}/`}
            >
              {image}
            </Link>
          ) : (
            image
          )}

          {/* MOBILE */}
          <div className="absolute inset-x-0 bottom-0 p-6 lg:hidden">
            <div className="rounded-xl bg-white/85 p-4 shadow-lg backdrop-blur-md">
              {mainTag && tag}

              <h1 className="mt-4 text-xl font-semibold text-grey-90">
                {title}
              </h1>

              {author}
            </div>
          </div>
        </div>

        {/* DESKTOP */}
        <div className="hidden lg:block">
          <div className="space-y-6 pr-4">
            {mainTag && tag}

            <h1 className="text-3xl leading-tight font-semibold text-heading md:text-4xl">
              {title}
            </h1>

            {author}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedBlogPost;
